/**
 * Modal Service Context
 * 
 * Provides centralized modal state management and programmatic modal control.
 * Similar pattern to NotificationContext.
 */

import React, { 
  createContext, 
  useState, 
  useCallback, 
  useRef,
  useMemo,
  ComponentType,
} from 'react';

import {
  ModalInstance,
  ModalId,
  ModalServiceConfig,
  ModalServiceContextValue,
  ModalServiceProviderProps,
  ModalOpenOptions,
} from './types';

import { ModalStackManager } from './ModalStackManager';

/**
 * Default modal service configuration
 */
const DEFAULT_CONFIG: ModalServiceConfig = {
  baseZIndex: 9000,
  maxStack: 3,
  animationDuration: 300,
  closeOnEscape: true,
};

/**
 * Create the context
 */
const ModalServiceContext = createContext<ModalServiceContextValue | undefined>(undefined);

/**
 * Modal Service Provider Component
 */
export const ModalServiceProvider: React.FC<ModalServiceProviderProps> = ({
  children,
  config: userConfig = {},
}) => {
  // Merge user config with defaults
  const [config, setConfig] = useState<ModalServiceConfig>({
    ...DEFAULT_CONFIG,
    ...userConfig,
  });

  // Active modals state
  const [activeModals, setActiveModals] = useState<Map<ModalId, ModalInstance>>(new Map());

  // Stack manager for z-index calculation
  const stackManager = useRef(
    new ModalStackManager(config.baseZIndex, config.maxStack)
  );

  // Counter for generating unique IDs
  const modalIdCounter = useRef(0);

  /**
   * Generate unique modal ID
   */
  const generateId = useCallback((): ModalId => {
    modalIdCounter.current += 1;
    return `modal-${modalIdCounter.current}-${Date.now()}`;
  }, []);

  /**
   * Update configuration
   */
  const updateConfig = useCallback((newConfig: Partial<ModalServiceConfig>) => {
    setConfig(prev => {
      const updated = { ...prev, ...newConfig };
      
      // Update stack manager if relevant config changed
      if (newConfig.baseZIndex !== undefined) {
        stackManager.current.setBaseZIndex(newConfig.baseZIndex);
      }
      if (newConfig.maxStack !== undefined) {
        stackManager.current.setMaxStack(newConfig.maxStack);
      }
      
      return updated;
    });
  }, []);

  /**
   * Open a modal programmatically
   */
  const open = useCallback(<P = any>(
    component: ComponentType<P>,
    props: Omit<P, 'isOpen' | 'onClose'>,
    options: ModalOpenOptions = {}
  ): ModalId => {
    const modalId = options.id || generateId();

    // Check if we can add to stack
    if (!stackManager.current.canAddToStack(activeModals.size)) {
      return modalId;
    }

    // Calculate z-index for this modal
    const zIndex = stackManager.current.calculateZIndex(activeModals.size);

    // Store current focused element for restoration
    const launcherElement = options.preserveFocus !== false 
      ? (document.activeElement as HTMLElement) 
      : null;

    // Create modal instance
    const modalInstance: ModalInstance<P> = {
      id: modalId,
      component,
      props: props as P,
      zIndex,
      state: 'opening',
      launcherElement,
    };

    // Add to active modals
    setActiveModals(prev => {
      const updated = new Map(prev);
      updated.set(modalId, modalInstance);
      return updated;
    });

    // Transition to open state after brief delay
    setTimeout(() => {
      setActiveModals(prev => {
        const updated = new Map(prev);
        const existing = updated.get(modalId);
        if (existing) {
          updated.set(modalId, { ...existing, state: 'open' });
        }
        return updated;
      });
    }, 50);

    // Call onOpen callback if provided
    if (options.onOpen) {
      options.onOpen();
    }

    return modalId;
  }, [activeModals.size, config.maxStack, generateId]);

  /**
   * Close a specific modal
   */
  const close = useCallback((id: ModalId) => {
    const modal = activeModals.get(id);
    if (!modal) return;

    // Remove immediately - PrimaryModal handles its own close animations
    setActiveModals(prev => {
      const updated = new Map(prev);
      updated.delete(id);
      return updated;
    });

    // Restore focus to launcher element (small delay to allow modal cleanup)
    setTimeout(() => {
      if (modal.launcherElement && document.contains(modal.launcherElement)) {
        modal.launcherElement.focus();
      }
    }, 50);
  }, [activeModals]);

  /**
   * Close all modals
   */
  const closeAll = useCallback(() => {
    activeModals.forEach((_, id) => {
      close(id);
    });
  }, [activeModals, close]);

  /**
   * Check if a modal is open
   */
  const isOpen = useCallback((id: ModalId): boolean => {
    return activeModals.has(id);
  }, [activeModals]);

  /**
   * Get the topmost modal
   */
  const getTopmost = useCallback((): ModalInstance | null => {
    return stackManager.current.getTopmost(activeModals);
  }, [activeModals]);

  /**
   * Context value
   */
  const contextValue = useMemo<ModalServiceContextValue>(() => ({
    config,
    activeModals,
    open,
    close,
    closeAll,
    isOpen,
    getTopmost,
    updateConfig,
  }), [
    config,
    activeModals,
    open,
    close,
    closeAll,
    isOpen,
    getTopmost,
    updateConfig,
  ]);

  return (
    <ModalServiceContext.Provider value={contextValue}>
      {children}
    </ModalServiceContext.Provider>
  );
};

/**
 * Export context for hook usage
 */
export { ModalServiceContext };

